<template>
  <main class="rm-page service-detail-page">
    <header class="rm-topbar rm-topbar--back-only">
      <button
        class="rm-icon-button"
        type="button"
        aria-label="Volver"
        @click="goBack"
      >
        <span aria-hidden="true"></span>
      </button>

      <div class="rm-heading">
        <h1 class="rm-title">Detalle del servicio</h1>
      </div>
    </header>

    <section class="service-detail-state-shell" aria-live="polite">
      <div v-if="isLoading" class="service-detail-state">
        <span class="service-detail-spinner" aria-hidden="true"></span>
        <p>Cargando parada...</p>
      </div>

      <div v-else-if="hasError" class="service-detail-state">
        <h2>No se pudo cargar la parada</h2>
        <p>{{ errorMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isLoading"
          @click="loadRouteStop"
        >
          Reintentar
        </button>
      </div>

      <div v-else-if="hasEmptyState" class="service-detail-state">
        <h2>Sin datos de la parada</h2>
        <p>{{ emptyMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isLoading"
          @click="loadRouteStop"
        >
          Reintentar
        </button>
      </div>

      <section
        v-else
        class="service-detail-content"
        aria-label="Detalle de parada"
      >
        <div class="service-detail-scroll">
          <p v-if="packageChipsText" class="rm-chips">
            {{ packageChipsText }}
          </p>
          <section class="service-detail-hero">
            <h2>{{ displayAddress || "Dirección no disponible" }}</h2>
          </section>

          <article class="service-detail-card service-detail-card--outlined">
            <header class="service-detail-card__header">
              <span
                class="service-detail-card__icon service-detail-card__icon--info"
                aria-hidden="true"
                >i</span
              >
              <span>DETALLE</span>
            </header>

            <div class="service-detail-card__body">
              <h3>{{ packagesLabel }}</h3>
              <p>{{ scheduleLabel }}</p>
            </div>
          </article>

          <article class="service-detail-card service-detail-card--outlined">
            <header class="service-detail-card__header">
              <span
                class="service-detail-card__icon service-detail-card__icon--user"
                aria-hidden="true"
              ></span>
              <span>CLIENTE</span>
            </header>

            <div class="service-detail-card__body">
              <h3 v-if="contactName">{{ contactName }}</h3>
              <p v-if="attentionOf">{{ attentionOf }}</p>
              <p v-if="phoneNumber">{{ phoneNumber }}</p>
              <p v-if="!hasAnyContactData">Sin datos de cliente.</p>
            </div>
          </article>

          <article class="service-detail-card service-detail-card--outlined">
            <header class="service-detail-card__header">
              <span
                class="service-detail-card__icon service-detail-card__icon--pin"
                aria-hidden="true"
              ></span>
              <span>DIRECCIÓN NORMALIZADA</span>
            </header>

            <div class="service-detail-card__body">
              <h3 v-if="normalizedAddress">{{ normalizedAddress }}</h3>
              <p v-if="normalizedAddressLabel">{{ normalizedAddressLabel }}</p>
              <p v-if="!hasNormalizedAddressData">Sin dirección normalizada.</p>
            </div>
          </article>
        </div>

        <footer
          class="service-detail-footer"
          :class="{ 'service-detail-footer--single': isLoadUnload }"
        >
          <template v-if="isLoadUnload">
            <button
              class="rm-button rm-button--outline service-detail-footer__button"
              type="button"
              @click="openLoadUnloadFinalize"
            >
              <img
                class="service-detail-footer__icon rm-button__icon-img"
                :src="sendIcon"
                alt=""
                aria-hidden="true"
              />
              <span>Finalizar {{ stopTypeLabel }}</span>
            </button>
          </template>
          <template v-else>
            <button
              class="rm-button rm-button--outline service-detail-footer__button"
              type="button"
              @click="openPackagesScan"
            >
              <img
                class="service-detail-footer__icon rm-button__icon-img"
                :src="scanIcon"
                alt=""
                aria-hidden="true"
              />
              <span>Parcial</span>
            </button>
            <button
              class="rm-button rm-button--outline service-detail-footer__button"
              type="button"
              @click="openFinishForm"
            >
              <img
                class="service-detail-footer__icon rm-button__icon-img"
                :src="sendIcon"
                alt=""
                aria-hidden="true"
              />
              <span>Total</span>
            </button>
          </template>
        </footer>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import scanIcon from "../../../../assets/img/mobile-images/scan.svg";
import sendIcon from "../../../../assets/img/mobile-images/send.svg";
import { loadRouteStopDetail } from "../shared/loadRouteStopDetail";
import {
  buildPackageChips,
  formatPackageCount,
  resolveStopTypeLabel,
} from "../day-route/dayRouteModel";

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
  context: {
    type: Object,
    default() {
      return null;
    },
  },
});
const emit = defineEmits(["navigate", "back"]);
const planId = ref("");
const routeStopId = ref("");
const routeStop = ref(null);
const isLoading = ref(false);
const errorMessage = ref("");
const emptyMessage = ref("");
let abortController = null;

const hasError = computed(
  () => !isLoading.value && !routeStop.value && Boolean(errorMessage.value)
);
const hasEmptyState = computed(
  () =>
    !isLoading.value &&
    !hasError.value &&
    !routeStop.value &&
    Boolean(emptyMessage.value)
);
const displayAddress = computed(
  () =>
    routeStop.value?.stop.inputAddress || routeStop.value?.displayAddress || ""
);
const normalizedAddress = computed(
  () => routeStop.value?.stop.addressNormalized.displayAddress ?? ""
);
const normalizedAddressLabel = computed(
  () => routeStop.value?.stop.addressNormalized.label ?? ""
);
const packageChipsText = computed(() =>
  buildPackageChips(routeStop.value).join(" · ")
);
const packagesLabel = computed(() =>
  formatPackageCount(routeStop.value?.stop.totalTasks ?? 0)
);
const scheduleLabel = computed(() => routeStop.value?.scheduleLabel ?? "");
const isLoadUnload = computed(() => routeStop.value?.stop.isLoadUnload ?? false);
const stopTypeLabel = computed(() =>
  resolveStopTypeLabel(routeStop.value?.stop.manualType, routeStop.value?.stop.type)
);
const contactName = computed(() => routeStop.value?.stop.contact?.name ?? "");
const attentionOf = computed(
  () => routeStop.value?.stop.contact?.attentionOf ?? ""
);
const phoneNumber = computed(() => routeStop.value?.stop.contact?.phone ?? "");
const hasAnyContactData = computed(() =>
  Boolean(contactName.value || attentionOf.value || phoneNumber.value)
);
const hasNormalizedAddressData = computed(() =>
  Boolean(normalizedAddress.value || normalizedAddressLabel.value)
);

onMounted(() => {
  readRouteQuery();

  if (props.embedded && props.context?.routeStop) {
    routeStop.value = props.context.routeStop;
    return;
  }

  loadRouteStop();
});

onBeforeUnmount(() => {
  abortCurrentRequest();
});

async function loadRouteStop() {
  if (isLoading.value) {
    return;
  }

  if (!planId.value || !routeStopId.value) {
    clearRouteStop();
    errorMessage.value = "No se recibió el plan o la parada en la URL.";
    return;
  }

  const vehicleId = (props.context?.vehicleId || "").toString();

  abortCurrentRequest();
  abortController = new AbortController();

  isLoading.value = true;
  errorMessage.value = "";
  emptyMessage.value = "";
  routeStop.value = null;

  try {
    const loadedRouteStop = await loadRouteStopDetail({
      routeStop: props.context?.routeStop ?? null,
      planId: planId.value,
      routeStopId: routeStopId.value,
      vehicleId,
      signal: abortController.signal,
    });

    if (!loadedRouteStop) {
      emptyMessage.value =
        "No hay datos disponibles para la parada seleccionada.";
      return;
    }

    routeStop.value = loadedRouteStop;
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    errorMessage.value = normalizeLoadErrorMessage(error.message);
  } finally {
    isLoading.value = false;
    abortController = null;
  }
}

function openPackagesScan() {
  if (!routeStop.value) {
    return;
  }

  if (props.embedded) {
    emit("navigate", {
      view: "scan-packages",
      payload: {
        planId: planId.value,
        routeStopId: routeStop.value.stop.stopId,
        stopId: routeStop.value.stop.stopId,
        routeStop: routeStop.value,
      },
    });
    return;
  }

  navigateToFutureRoute("/packages-scan", {
    planId: planId.value,
    stopId: routeStop.value.stop.stopId,
  });
}

function openFinishForm() {
  if (!routeStop.value) {
    return;
  }

  if (props.embedded) {
    emit("navigate", {
      view: "finish-form",
      payload: {
        planId: planId.value,
        routeStopId: routeStop.value.stop.stopId,
        stopId: routeStop.value.stop.stopId,
        routeStop: routeStop.value,
        completionMode: "direct",
      },
    });
    return;
  }

  navigateToFutureRoute("/no-scan", {
    planId: planId.value,
    stopId: routeStop.value.stop.stopId,
    mode: "direct",
  });
}

function openLoadUnloadFinalize() {
  if (!routeStop.value) {
    return;
  }

  if (props.embedded) {
    emit("navigate", {
      view: "finish-load-unload",
      payload: {
        planId: planId.value,
        routeStopId: routeStop.value.stop.stopId,
        stopId: routeStop.value.stop.stopId,
        routeStop: routeStop.value,
      },
    });
    return;
  }

  navigateToFutureRoute("/finish-load-unload", {
    planId: planId.value,
    stopId: routeStop.value.stop.stopId,
  });
}

function goBack() {
  if (props.embedded) {
    emit("back");
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  navigateToFutureRoute("/", { planId: planId.value });
}

function navigateToFutureRoute(path, query) {
  const target = new URL(path, window.location.origin);

  Object.entries(query).forEach(([key, value]) => {
    if (value) {
      target.searchParams.set(key, value);
    }
  });

  window.location.assign(target.toString());
}

function readRouteQuery() {
  if (props.embedded) {
    planId.value = props.context?.planId?.trim?.() ?? "";
    routeStopId.value =
      (props.context?.routeStopId || props.context?.stopId || "").trim?.() ??
      "";
    return;
  }

  const query = new URLSearchParams(window.location.search);
  planId.value = query.get("planId")?.trim() ?? "";
  routeStopId.value = query.get("stopId")?.trim() ?? "";
}

function clearRouteStop() {
  routeStop.value = null;
  emptyMessage.value = "";
}

function normalizeLoadErrorMessage(message) {
  if (!message) {
    return "No se pudieron cargar los datos de la parada.";
  }

  if (
    message.toLowerCase().includes("paradas del plan") ||
    message.toLowerCase().includes("tareas de la parada") ||
    message.toLowerCase().includes("paquetes de la parada")
  ) {
    return "No se pudieron cargar los datos de la parada.";
  }

  return message.trim();
}

function abortCurrentRequest() {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}
</script>

<style scoped src="./serviceDetail.css"></style>
<style scoped src="../shared/routesMobileCommon.css"></style>
